import React, { useMemo, useState, createContext, useContext, memo } from 'react';
// ---------------------------------------------------------------------------
// Shared source of truth for the maintenance + extraordinary-request side of
// CleanAI. Mirrors PlatformContext: in-memory state, actions ripple across the
// Host "Richiedi intervento" screens and the Manutentore role (/man).
// ---------------------------------------------------------------------------
export type Availability = 'disponibile' | 'occupato' | 'off';
export type Trade = 'Idraulico' | 'Elettricista' | 'Caldaista' | 'Tuttofare' | 'Fabbro';
export interface Provider {
  id: string;
  name: string;
  trade: Trade;
  zones: string[];
  availability: Availability;
  rating: number;
  // First availability label, e.g. "Oggi 16:00"
  firstSlot: string;
  distanceKm: number;
}
export type RequestType = 'pulizia-straordinaria' | 'manutenzione';
export type Urgency = 'urgente' | 'normale';
export type RequestStatus = 'in_attesa_aggregazione' | 'in_ricerca' | 'assegnata' | 'pianificata' | 'in_corso' | 'completata';
export interface Aggregation {
  filled: number;
  total: number;
  minHosts: number;
  estimateDate: string;
}
export interface MaintenanceRequest {
  id: string;
  title: string;
  detail: string;
  property: string;
  zone: string;
  type: RequestType;
  trade?: Trade;
  urgency: Urgency;
  status: RequestStatus;
  aggregation?: Aggregation;
  providerId: string | null;
  providerName: string | null;
  date: string;
  price: number;
  createdAt: string;
}
export type JobStatus = 'da_accettare' | 'accettata' | 'in_corso' | 'completata';
export interface MaintenanceJob {
  id: string;
  requestId: string | null;
  title: string;
  trade: Trade;
  property: string;
  hostName: string;
  zone: string;
  type: string;
  date: string;
  time: string;
  status: JobStatus;
  earning: number;
  distanceKm: number;
  // light "why we propose this" signals
  hostsInZone?: number;
  efficiency?: string;
}
export function initials(name: string): string {
  return name.split(' ').map(p => p[0]).filter(Boolean).slice(0, 2).join('');
}
function shortZone(zone: string): string {
  const parts = zone.split('·');
  return (parts[parts.length - 1] || zone).trim();
}
const INITIAL_PROVIDERS: Provider[] = [{
  id: 'p1',
  name: 'Andrea Conti',
  trade: 'Idraulico',
  zones: ['Navigli', 'Tortona'],
  availability: 'disponibile',
  rating: 4.8,
  firstSlot: 'Oggi 16:00',
  distanceKm: 0.6
}, {
  id: 'p2',
  name: 'Marco Vitale',
  trade: 'Elettricista',
  zones: ['Isola', 'Centro'],
  availability: 'disponibile',
  rating: 4.7,
  firstSlot: 'Oggi 18:30',
  distanceKm: 1.1
}, {
  id: 'p3',
  name: 'Giorgio Sala',
  trade: 'Caldaista',
  zones: ['Navigli', 'Porta Romana'],
  availability: 'occupato',
  rating: 4.9,
  firstSlot: 'Domani 09:00',
  distanceKm: 1.8
}, {
  id: 'p4',
  name: 'Luca Ferri',
  trade: 'Tuttofare',
  zones: ['Centro', 'Tortona'],
  availability: 'disponibile',
  rating: 4.6,
  firstSlot: 'Oggi 17:15',
  distanceKm: 0.9
}];
const INITIAL_REQUESTS: MaintenanceRequest[] = [{
  id: 'r1',
  title: 'Revisione caldaia',
  detail: 'Revisione annuale obbligatoria caldaia a gas.',
  property: 'Loft Navigli',
  zone: 'Navigli',
  type: 'manutenzione',
  trade: 'Caldaista',
  urgency: 'normale',
  status: 'in_attesa_aggregazione',
  aggregation: {
    filled: 3,
    total: 5,
    minHosts: 5,
    estimateDate: '28 Gen'
  },
  providerId: null,
  providerName: null,
  date: 'Da definire',
  price: 70,
  createdAt: 'Ieri'
}, {
  id: 'r2',
  title: 'Perdita rubinetto cucina',
  detail: 'Il rubinetto perde, serve intervento idraulico.',
  property: 'Bilo Porta Romana',
  zone: 'Porta Romana',
  type: 'manutenzione',
  trade: 'Idraulico',
  urgency: 'urgente',
  status: 'assegnata',
  providerId: 'p1',
  providerName: 'Andrea Conti',
  date: 'Oggi 16:00',
  price: 90,
  createdAt: 'Oggi'
}, {
  id: 'r3',
  title: 'Pulizia straordinaria post-festa',
  detail: 'Deep cleaning urgente prima del prossimo check-in.',
  property: 'Studio Isola',
  zone: 'Isola',
  type: 'pulizia-straordinaria',
  urgency: 'urgente',
  status: 'in_ricerca',
  providerId: null,
  providerName: null,
  date: 'Oggi',
  price: 120,
  createdAt: 'Oggi'
}];
// Open aggregations other hosts can join (separate from the user's own requests).
const INITIAL_OPEN_AGGREGATIONS: MaintenanceRequest[] = [{
  id: 'agg1',
  title: 'Riparazione serrature',
  detail: 'Fabbro in zona per sostituzione serrature.',
  property: '—',
  zone: 'Navigli',
  type: 'manutenzione',
  trade: 'Fabbro',
  urgency: 'normale',
  status: 'in_attesa_aggregazione',
  aggregation: {
    filled: 1,
    total: 4,
    minHosts: 4,
    estimateDate: '30 Gen'
  },
  providerId: null,
  providerName: null,
  date: 'Da definire',
  price: 60,
  createdAt: '2 giorni fa'
}, {
  id: 'agg2',
  title: 'Controllo impianto idraulico',
  detail: 'Idraulico in zona per controllo generale.',
  property: '—',
  zone: 'Isola',
  type: 'manutenzione',
  trade: 'Idraulico',
  urgency: 'normale',
  status: 'in_attesa_aggregazione',
  aggregation: {
    filled: 2,
    total: 4,
    minHosts: 4,
    estimateDate: '2 Feb'
  },
  providerId: null,
  providerName: null,
  date: 'Da definire',
  price: 55,
  createdAt: '3 giorni fa'
}];
const INITIAL_JOBS: MaintenanceJob[] = [{
  id: 'mj1',
  requestId: 'r2',
  title: 'Perdita rubinetto cucina',
  trade: 'Idraulico',
  property: 'Bilo Porta Romana',
  hostName: 'Massimo R.',
  zone: 'Porta Romana',
  type: 'Riparazione idraulica',
  date: 'Oggi',
  time: '16:00',
  status: 'accettata',
  earning: 90,
  distanceKm: 0.7
}, {
  id: 'mj2',
  requestId: null,
  title: 'Sostituzione faretti LED',
  trade: 'Elettricista',
  property: 'Trilo Centro',
  hostName: 'Giulia P.',
  zone: 'Centro',
  type: 'Intervento elettrico',
  date: 'Ieri',
  time: '10:00',
  status: 'completata',
  earning: 75,
  distanceKm: 1.2
}];
// Provider-side opportunities (the matching engine surfaces these)
export interface MaintenanceOpportunity {
  id: string;
  category: 'aggregabile' | 'vicina' | 'combinazione';
  trade: Trade;
  title: string;
  zone: string;
  distanceKm: number;
  suggestedPrice: number;
  jobsCount: number;
  window: string;
  hostsInZone: number;
  efficiency: string;
}
const INITIAL_OPPORTUNITIES: MaintenanceOpportunity[] = [{
  id: 'op1',
  category: 'aggregabile',
  trade: 'Caldaista',
  title: 'Revisione caldaia',
  zone: 'Milano · Navigli',
  distanceKm: 1.2,
  suggestedPrice: 70,
  jobsCount: 4,
  window: 'Mar 28 Gen',
  hostsInZone: 4,
  efficiency: '+22%'
}, {
  id: 'op2',
  category: 'vicina',
  trade: 'Idraulico',
  title: 'Riparazione idraulica',
  zone: 'Milano · Porta Romana',
  distanceKm: 0.4,
  suggestedPrice: 90,
  jobsCount: 1,
  window: 'Oggi, 16:00',
  hostsInZone: 1,
  efficiency: '+18%'
}, {
  id: 'op3',
  category: 'combinazione',
  trade: 'Elettricista',
  title: 'Intervento elettrico',
  zone: 'Milano · Isola',
  distanceKm: 0.6,
  suggestedPrice: 65,
  jobsCount: 2,
  window: 'Mer 29 Gen',
  hostsInZone: 2,
  efficiency: '+18%'
}];
interface MaintenanceContextValue {
  providers: Provider[];
  requests: MaintenanceRequest[];
  openAggregations: MaintenanceRequest[];
  jobs: MaintenanceJob[];
  opportunities: MaintenanceOpportunity[];
  createRequest: (input: {
    title: string;
    detail?: string;
    property: string;
    zone: string;
    type: RequestType;
    trade?: Trade;
    urgency: Urgency;
    price: number;
    date?: string;
    providerId?: string | null;
  }) => MaintenanceRequest;
  joinAggregation: (aggregationId: string) => void;
  assignProvider: (requestId: string, providerId: string) => void;
  respondToRequest: (requestId: string) => void;
  acceptJob: (jobId: string) => void;
  startJob: (jobId: string) => void;
  completeJob: (jobId: string) => void;
  providersForZone: (zone: string, trade?: Trade) => Provider[];
}
const MaintenanceContext = createContext<MaintenanceContextValue | undefined>(undefined);
export function MaintenanceProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [providers] = useState<Provider[]>(INITIAL_PROVIDERS);
  const [requests, setRequests] = useState<MaintenanceRequest[]>(INITIAL_REQUESTS);
  const [openAggregations, setOpenAggregations] = useState<MaintenanceRequest[]>(INITIAL_OPEN_AGGREGATIONS);
  const [jobs, setJobs] = useState<MaintenanceJob[]>(INITIAL_JOBS);
  const [opportunities] = useState<MaintenanceOpportunity[]>(INITIAL_OPPORTUNITIES);
  const pushJobFromRequest = (req: MaintenanceRequest) => {
    const newJob: MaintenanceJob = {
      id: `mj-${Date.now()}-${Math.random().toString(36).slice(2, 5)}`,
      requestId: req.id,
      title: req.title,
      trade: req.trade ?? 'Tuttofare',
      property: req.property,
      hostName: 'Host CleanAI',
      zone: shortZone(req.zone),
      type: req.type === 'pulizia-straordinaria' ? 'Pulizia straordinaria' : 'Manutenzione',
      date: req.date || 'Da pianificare',
      time: '—',
      status: 'da_accettare',
      earning: req.price,
      distanceKm: 1
    };
    setJobs(prev => [newJob, ...prev]);
  };
  const createRequest: MaintenanceContextValue['createRequest'] = input => {
    const isAggregated = input.type === 'manutenzione' && input.urgency === 'normale';
    const req: MaintenanceRequest = {
      id: `r-${Date.now()}-${Math.random().toString(36).slice(2, 5)}`,
      title: input.title,
      detail: input.detail ?? '',
      property: input.property,
      zone: input.zone,
      type: input.type,
      trade: input.trade,
      urgency: input.urgency,
      status: isAggregated ? 'in_attesa_aggregazione' : input.providerId ? 'assegnata' : 'in_ricerca',
      aggregation: isAggregated ? {
        filled: 1,
        total: 4,
        minHosts: 4,
        estimateDate: 'Da definire'
      } : undefined,
      providerId: input.providerId ?? null,
      providerName: input.providerId ? providers.find(p => p.id === input.providerId)?.name ?? null : null,
      date: input.date ?? (isAggregated ? 'Da definire' : 'Oggi'),
      price: input.price,
      createdAt: 'Adesso'
    };
    setRequests(prev => [req, ...prev]);
    // Assigned (urgent w/ provider) requests immediately spawn a provider job.
    if (req.status === 'assegnata') {
      pushJobFromRequest(req);
    }
    return req;
  };
  const joinAggregation = (aggregationId: string) => {
    // Joining can target either an open aggregation or one of the user's own.
    const promote = (req: MaintenanceRequest): MaintenanceRequest => {
      if (!req.aggregation) return req;
      const filled = Math.min(req.aggregation.filled + 1, req.aggregation.total);
      const reachedMin = filled >= req.aggregation.minHosts;
      return {
        ...req,
        aggregation: {
          ...req.aggregation,
          filled
        },
        status: reachedMin ? 'pianificata' : req.status
      };
    };
    setOpenAggregations(prev => prev.map(r => r.id === aggregationId ? promote(r) : r));
    setRequests(prev => prev.map(r => r.id === aggregationId ? promote(r) : r));
    // If an open aggregation reached its minimum, generate the provider job.
    const target = openAggregations.find(r => r.id === aggregationId) ?? requests.find(r => r.id === aggregationId);
    if (target?.aggregation) {
      const willReach = target.aggregation.filled + 1 >= target.aggregation.minHosts;
      if (willReach) {
        pushJobFromRequest({
          ...target,
          status: 'pianificata'
        });
      }
    }
  };
  const assignProvider = (requestId: string, providerId: string) => {
    const provider = providers.find(p => p.id === providerId);
    setRequests(prev => prev.map(r => r.id === requestId ? {
      ...r,
      providerId,
      providerName: provider?.name ?? null,
      status: 'assegnata'
    } : r));
    const req = requests.find(r => r.id === requestId);
    if (req) {
      pushJobFromRequest({
        ...req,
        providerId,
        providerName: provider?.name ?? null,
        status: 'assegnata'
      });
    }
  };
  const respondToRequest = (requestId: string) => {
    const req = requests.find(r => r.id === requestId);
    if (!req) return;
    setRequests(prev => prev.map(r => r.id === requestId ? {
      ...r,
      providerId: 'me',
      providerName: 'Tu',
      status: 'assegnata'
    } : r));
    pushJobFromRequest({
      ...req,
      providerId: 'me',
      providerName: 'Tu',
      status: 'assegnata'
    });
  };
  const acceptJob = (jobId: string) => setJobs(prev => prev.map(j => j.id === jobId ? {
    ...j,
    status: 'accettata'
  } : j));
  const startJob = (jobId: string) => setJobs(prev => prev.map(j => j.id === jobId ? {
    ...j,
    status: 'in_corso'
  } : j));
  const completeJob = (jobId: string) => setJobs(prev => prev.map(j => j.id === jobId ? {
    ...j,
    status: 'completata'
  } : j));
  const providersForZone = (zone: string, trade?: Trade) => {
    const z = shortZone(zone).toLowerCase();
    return providers.filter(p => p.availability !== 'off' && (trade ? p.trade === trade : true) && p.zones.some(pz => pz.toLowerCase() === z));
  };
  const value = useMemo(() => ({
    providers,
    requests,
    openAggregations,
    jobs,
    opportunities,
    createRequest,
    joinAggregation,
    assignProvider,
    respondToRequest,
    acceptJob,
    startJob,
    completeJob,
    providersForZone
  }), [providers, requests, openAggregations, jobs, opportunities]);
  return <MaintenanceContext.Provider value={value} data-id="element-1183">
      {children}
    </MaintenanceContext.Provider>;
}
export function useMaintenance(): MaintenanceContextValue {
  const ctx = useContext(MaintenanceContext);
  if (!ctx) {
    throw new Error('useMaintenance must be used within a MaintenanceProvider');
  }
  return ctx;
}